
import { useEffect, useState } from 'react'
import CtaButton from '../../components/ctaButton/CtaButton'
import './style.scss'
import { useDispatch } from 'react-redux'
import { setShowWatchVideos } from '../../redux/reducers/chatSlice'
import { userStateData } from '../../data/userStateData';
import { useAppSelector } from '../../redux/hooks'
import { interactionConnector } from '../../server/connector/interactionConnector'
import { ChatInteractions, VideoUploadResponse } from '../../server/types';
import { API_URL } from '../../constants/pathConstants'
import { ChatMessage, InteractionStatus } from '../../types/types'
import { PAGE_URLS } from '../../constants/hrefLinks'
import { MAX_VIDEO_FILE_SIZE } from '../../constants/constants'

type InteractionJson = {
	messages?: ChatMessage[]
}

const MIN_INSTRUCTOR_MESSAGE_LENGTH: number = 250
const MAX_INSTRUCTOR_MESSAGE_LENGTH: number = 550

function DashboardChatContentArea() {
	const [messages, setMessages] = useState<ChatMessage[]>([])
	const [inputMessage, setInputMessage] = useState<string>('')
	const [sendingMessage, setSendingMessage] = useState<boolean>(false)
	const [uploadingVideo, setUploadingVideo] = useState<boolean>(false)
	const [interactionStatus, setInteractionStatus] = useState<string>('UNKNOWN')

	const chatDataLoaded: boolean = useAppSelector(state => state.user.chatDataLoaded)

	const [reviewRating, setReviewRating] = useState<number>(0)
	const [reviewAnalysisRating, setReviewAnalysisRating] = useState<number>(0)
	const [reviewCommunicationRating, setReviewCommunicationRating] = useState<number>(0)
	const [reviewQuickRepliesRating, setReviewQuickRepliesRating] = useState<number>(0)
	const [reviewEncouragementRating, setReviewEncouragementRating] = useState<number>(0)
	const [leavingReview, setLeavingReview] = useState<boolean>(false)
	const [requestTip, setRequestTip] = useState<boolean>(false)
	
	const [instructorMessageError, setInstructorMessageError] = useState<boolean>(false)
	const [videoSizeError, setVideoSizeError] = useState<boolean>(false)

	const [videoFiles, setVideoFiles] = useState<string[]>([])

	const [confirmingVideoFile, setConfirmingVideoFile] = useState<boolean>(false)
	const [confirmSendingMessage, setConfirmSendingMessage] = useState<boolean>(false)

	const dispatch = useDispatch()

	// Setup test values
	useEffect(() => {
		if (chatDataLoaded === true) {
			const interaction: ChatInteractions | undefined = userStateData.getInteractionData()
			if (interaction && interaction.interaction_data.length > 0) {
				setInteractionStatus(interaction.status)
				const sanatizedData: string = interaction.interaction_data.replace(/(\r\n|\n|\r)/gm, "") // .replace('\n', '') // .replace(/[\\]/g, '\\\\').replace(/[\"]/g, '\\\"').replace(/[\/]/g, '\\/').replace(/[\f]/g, '\\f').replace(/[\n]/g, '\\n').replace(/[\r]/g, '\\r').replace(/[\t]/g, '\\t')
				// console.log(interaction.interaction_data)
				const interactionObj: InteractionJson = JSON.parse(sanatizedData) // interaction.interaction_data)
				if (interactionObj.messages) {
					// count how many instructor messages have been sent
					setMessages(interactionObj.messages)
				}
			}
		}
	}, [chatDataLoaded])

	const sendMessageButtonClicked = () => {
		setConfirmSendingMessage(true)
		setConfirmingVideoFile(false)
	}

	const onSendMessageConfirm = () => {
		setSendingMessage(true)
		const currentInteraction: ChatInteractions = userStateData.getInteractions()[0]

		if (interactionConnector.requestInProgress) {
			console.log('[InstructorsProfile] - requestProfile() called while requestInProgress')
		} else {
			interactionConnector.interactionAddMessageRequest(currentInteraction.id, inputMessage).then((successRequest) => {
				setSendingMessage(false)
				if (!successRequest) {
					console.log('WARNING - [DashboardChatContentArea] - requestProfile() failed')
				} else {
					setInputMessage('')
					window.location.reload()
					// setMessages(newMessages)
				}
			})
		}
	}

	const leaveReviewButtonClicked = () => {
		setLeavingReview(true)
		const currentInteraction: ChatInteractions = userStateData.getInteractions()[0]
		if (interactionConnector.requestInProgress) {
			console.log('[InstructorsProfile] - addReviewRequest() called while requestInProgress')
		} else {
			interactionConnector.addReviewRequest(
				currentInteraction.instructor_id,
				currentInteraction.customer_id,
				inputMessage,
				reviewRating
			).then((successRequest) => {
				// setSendingMessage(false)
				if (!successRequest) {
					console.log('WARNING - [DashboardChatContentArea] - addReviewRequest() failed')
				} else {
					// setRequestTip(true)
					// setInteractionStatus(InteractionStatus.COMPLETE)
					interactionConnector.updateInteractionStatusRequest(currentInteraction.id, InteractionStatus.COMPLETE, currentInteraction.instructor_id).then((successRequest) => {
						setSendingMessage(false)
						if (!successRequest) {
							console.log('WARNING - [DashboardChatContentArea] - requestProfile() failed')
						} else {
							setInteractionStatus(InteractionStatus.COMPLETE)
							setRequestTip(true)
						}
					})
				}
			})
		}
	}

	const leaveTipClickHandler = (amount: number) => {
		const querySting: string = window.location.search;
		const urlParams: URLSearchParams = new URLSearchParams(querySting)
		const profileId: string | null = urlParams.get('id')
		if (amount === 0 || !profileId) {
			window.location.replace(PAGE_URLS.DASHBOARD_CUSTOMER)
		} else {
			if (interactionConnector.requestInProgress) {
				console.log('[DashboardChatContentArea] - leaveTipClickHandler() called while requestInProgress')
			} else {
				interactionConnector.performTipInteractionRequest(
					profileId,
					amount
				).then((requestStatus) => {
					if (!requestStatus) {
						console.log('[CreateInteraction] - createNewInteraction() call failed')
					}
					window.location.replace(userStateData.transactionUrl)
				})
			}
		}
	}

	const videoClickHandler = (fileName: string) => {
		userStateData.setVideoFileName(fileName)
		dispatch(setShowWatchVideos(true))
	}

	const timeSinceMessage = (timeStamp: string) => {
		let currentDate: Date = new Date()
		let messageTime: Date = new Date(parseInt(timeStamp))
		// more than 1 day
		if (currentDate.getTime() - messageTime.getTime() > 86400000) {
			return `${Math.floor((currentDate.getTime() - messageTime.getTime()) / 86400000)} day`
		} else if (currentDate.getTime() - messageTime.getTime() > 3600000) {
			return `${Math.floor((currentDate.getTime() - messageTime.getTime()) / 3600000)} hour(s)`
		} else if (currentDate.getTime() - messageTime.getTime() > 60000) {
			return `${Math.floor((currentDate.getTime() - messageTime.getTime()) / 60000)} mins`
		} else {
			return `${Math.floor((currentDate.getTime() - messageTime.getTime()) / 1000)} secs`
		}
	}

	const uploadFile = (file: File) => {
		console.log("Uploading file...")
		setUploadingVideo(true)
		const API_ENDPOINT = `${API_URL}upload-video`
		const request = new XMLHttpRequest()
		const formData = new FormData()

		request.open("POST", API_ENDPOINT, true)
		request.onreadystatechange = () => {
			if (request.readyState === 4 && request.status === 200) {
				const responseObj: VideoUploadResponse = JSON.parse(request.responseText)
				if (responseObj.error) {
					setUploadingVideo(false)
				} else if (responseObj.fileName) {
					videoAdded(responseObj.fileName)
				}
			} else if (request.status === 500) {
				setUploadingVideo(false)
			}
		}
		formData.append("file", file);
		request.send(formData);
	}

	const videoAdded = (newFileName: string) => {
		let newVideoList: string[] = [...videoFiles, newFileName]
		setVideoFiles(newVideoList)
		setUploadingVideo(false)
	}

	const removeVideoClickHandler = (fileIndex: number) => {
		let newList: string[] = []
		videoFiles.forEach((path: string, index: number) => {
			if (fileIndex !== index) {
				newList.push(path)
			}
		})
		setVideoFiles(newList)
	}

	// const onSendVideoClick = () => {

	// }

	// const commitVideos = (fileName: string) => {
	const onSendVideoClick = () => {
		setConfirmSendingMessage(true)
		setConfirmingVideoFile(true)
	}

	const onSendVideoConfirm = () => {
		setSendingMessage(true)
		const currentInteraction: ChatInteractions = userStateData.getInteractions()[0]

		if (interactionConnector.requestInProgress) {
			console.log('[InstructorsProfile] - requestProfile() called while requestInProgress')
		} else {
			interactionConnector.interactionAddVideoRequest(currentInteraction.id, videoFiles).then((successRequest) => {
				setSendingMessage(false)
				if (!successRequest) {
					console.log('WARNING - [DashboardChatContentArea] - requestProfile() failed')
				} else {
					setInputMessage('')
					window.location.reload()
					// setMessages(newMessages)
				}
			})
		}
	}

	const isMessageSender = (sourceId: string): boolean => {
		if (sourceId == '0') {
			if (userStateData.isAnInstructor()) {
				return true
			} else {
				return false
			}
		} else {
			if (userStateData.isAnInstructor()) {
				return false
			} else {
				return true
			}
		}
	}

	const confirmPopUpConfirm = () => {
		setConfirmSendingMessage(false)
		if (confirmingVideoFile) {
			onSendVideoConfirm()
		} else {
			onSendMessageConfirm()
		}
	}

	return (
		<div className='dashboard-content-area-wrap'>
			<div className='dashboard-content-area-chat-messages'>
				<div
					className='dashboard-content-area-chat-content'
				>
					{messages.length === 0 ?
						<span>No Active Messages</span>
						:
						messages.map((message: ChatMessage) =>
							<div
								className={isMessageSender(message.sourceId) ? 'dashboard-content-area-chat-message dashboard-content-area-chat-message-sender' : 'dashboard-content-area-chat-message'}
							>
								<img
									className='dashboard-content-area-chat-message-sender-icon'
									alt='profile'
									src={message.profileImgUrl}
								/>
								{/* <div className='dashboard-content-area-chat-message-text-area'> */}
								<div className={isMessageSender(message.sourceId) ? 'dashboard-content-area-chat-message-text-area dashboard-content-area-chat-message-text-area-other' : 'dashboard-content-area-chat-message-text-area'}>
									<div>{
										message.type == 'video' ?
											<>
												<div
													className='mini-video'
													onClick={() => videoClickHandler(message.message)}
												>
													<img
														className='upload-mini-video-play-icon'
														src='/imgs/icons/icon-play-circle.svg'
														alt='Play'
													/>
												</div>
												Video File
											</> :
											message.message.replaceAll('\\"', '"').replaceAll("\\'", "'")
									}</div>
									<div className='dashboard-content-area-chat-message-time'>{timeSinceMessage(message.time)}</div>
								</div>
							</div>
						)
					}
				</div>
			</div>
			{userStateData.isAnInstructor() && (interactionStatus === InteractionStatus.ACTIVE || interactionStatus === InteractionStatus.WAITING_FOR_VIDEOS) &&
				<div className='dashboard-content-area-chat-input-wrap'>
					{userStateData.isAnInstructor() && interactionStatus === InteractionStatus.ACTIVE &&
						<>
							<textarea
								className='dashboard-content-area-chat-input-input dashboard-content-area-chat-input-textarea dashboard-content-area-chat-input-textarea-small'
								onInput={(evt) => {
									evt.currentTarget.style.height = ""
									evt.currentTarget.style.height = evt.currentTarget.scrollHeight + "px"
								}}
								placeholder='Chat Now'
								value={inputMessage}
								onChange={(evt) => {
									const textWords: string[] = evt.target.value.split(' ')
									setInstructorMessageError(textWords.length < MIN_INSTRUCTOR_MESSAGE_LENGTH || textWords.length > MAX_INSTRUCTOR_MESSAGE_LENGTH)
									setInputMessage(evt.target.value)
								}}
								disabled={sendingMessage || uploadingVideo}
							/>
							{instructorMessageError &&
								<div className='dashboard-warning-message-txt m-l-20'>
									Message must be between {MIN_INSTRUCTOR_MESSAGE_LENGTH} and {MAX_INSTRUCTOR_MESSAGE_LENGTH} words.
								</div>
							}
						</>
					}
					{userStateData.isAnInstructor() && interactionStatus.toString() == 'WAITING_FOR_VIDEOS' &&
						<>
							<div 
								className='video-list-wrap'
							>
								{videoFiles.map((filePath: string, index: number) => 
									// <div>Video {filePath}</div>
									<div
										className='mini-video'
									>
										<img
											className='upload-mini-video-play-icon'
											src='/imgs/icons/icon-play-circle.svg'
											alt='Play'
											onClick={() => videoClickHandler(filePath)}
										/>
										<img
											className='upload-mini-video-remove-icon'
											src='/imgs/icons/icon-delete-x.svg'
											alt='Remove'
											onClick={() => {
												removeVideoClickHandler(index)
											}}
										/>
									</div>
								)}
							</div>
							{uploadingVideo ? 
								<img
									src='/imgs/misc/spinner.svg'
									className='upload-spinner'
									alt='spinner'
								/>
								:
								<div className='dashboard-content-area-chat-add-video-button-wrap'>
									{uploadingVideo === false && videoFiles.length < 3 &&
										<label
											className='dashboard-content-area-chat-add-video-button'
										>
											<input
												// onClick={() => addVideoButtonClicked()}
												type='file'
												id='videoFile'
												name='videoFile'
												onChange={(event) => {
													// videoAdded()
													if (event.target.files && event.target.files.length > 0) {
														if (event.target.files[0].size > MAX_VIDEO_FILE_SIZE) {
															setVideoSizeError(true)
														} else {
															setVideoSizeError(false)
															uploadFile(event.target.files[0])
														}
													}
												}}
												accept='video/*'
												disabled={sendingMessage || uploadingVideo}
											/>
											<div className='dashboard-content-area-chat-add-video-img-wrap'>
												Add 
												<img
													src='/imgs/icons/icon-faq-plus.svg'
													className='dashboard-content-area-chat-add-video-img'
													alt='Add video'
												/>
											</div>
										</label>
									}
									{videoFiles.length > 0 &&
										<CtaButton
											label='Send'
											size='small'
											onClick={() => {
												onSendVideoClick()
											}}
										/>
									}
								</div>
							}
							{videoSizeError &&
								<div className='dashboard-warning-message-txt m-l-20'>
									Error - File to large, videos can't be more than ${Math.floor(MAX_VIDEO_FILE_SIZE / 1024 / 1024)} MiB
								</div>
							}
						</>
					}
					{/* {userStateData.isAnInstructor() && instructorResponded === false && */}
					{userStateData.isAnInstructor() && interactionStatus.toString() == 'ACTIVE' &&
						<button
							className='dashboard-content-area-chat-send-message-button'
							disabled={inputMessage.length < 1 || sendingMessage || uploadingVideo || instructorMessageError}
							onClick={() => sendMessageButtonClicked()}
						>
							<img
								src='/imgs/icons/icon-send-message.svg'
								className='dashboard-content-area-chat-send-message-img'
								alt='send message'
							/>
						</button>
					}
					{/* <CtaButton
						label='End Interaction'
						onClick={() => {
							endInteractionButtonClicked()
						}}
						size='medium'
					/> */}
				</div>
			}
			{userStateData.isAnInstructor() === false && interactionStatus === InteractionStatus.REVIEW &&
				<div className='dashboard-content-area-review-input-wrap'>
					<input
						className='dashboard-content-area-chat-input-input'
						placeholder='Write A Review'
						value={inputMessage}
						onChange={(evt) => setInputMessage(evt.target.value)}
						disabled={sendingMessage || uploadingVideo}
					/>
					<div className='dashboard-content-area-review-review-wrap'>
						<div className='instructor-profile-review-card-rating-wrap'>
							<div className='instructor-profile-review-card-rating-star-wrap'>
								{
									[...Array(5)].map((_, index) => 
										<img 
											className='instructor-profile-review-card-rating-star-image'
											key={`reviewStar${reviewRating}-${index}`}
											src={reviewRating > index ? '/imgs/icons/icon-star.svg' : '/imgs/icons/icon-star-empty.svg'}
											alt='star'
											onClick={() => {
												setReviewRating(index + 1)
											}}
										/>
									)
								}
							</div>
							<div className='instructor-profile-review-card-rating-name dashboard-content-area-review-review-card-rating-name'>Overall Rating</div>
						</div>
						<div className='instructor-profile-review-card-rating-wrap'>
							<div className='instructor-profile-review-card-rating-star-wrap'>
								{
									[...Array(5)].map((_, index) => 
										<img 
											className='instructor-profile-review-card-rating-star-image'
											key={`reviewStar${reviewAnalysisRating}-${index}`}
											src={reviewAnalysisRating > index ? '/imgs/icons/icon-star.svg' : '/imgs/icons/icon-star-empty.svg'}
											alt='star'
											onClick={() => {
												setReviewAnalysisRating(index + 1)
											}}
										/>
									)
								}
							</div>
							<div className='instructor-profile-review-card-rating-name dashboard-content-area-review-review-card-rating-name'>Analysis Rating</div>
						</div>
						<div className='instructor-profile-review-card-rating-wrap'>
							<div className='instructor-profile-review-card-rating-star-wrap'>
								{
									[...Array(5)].map((_, index) => 
										<img 
											className='instructor-profile-review-card-rating-star-image'
											key={`reviewStar${reviewCommunicationRating}-${index}`}
											src={reviewCommunicationRating > index ? '/imgs/icons/icon-star.svg' : '/imgs/icons/icon-star-empty.svg'}
											alt='star'
											onClick={() => {
												setReviewCommunicationRating(index + 1)
											}}
										/>
									)
								}
							</div>
							<div className='instructor-profile-review-card-rating-name dashboard-content-area-review-review-card-rating-name'>Communication Rating</div>
						</div>
						<div className='instructor-profile-review-card-rating-wrap'>
							<div className='instructor-profile-review-card-rating-star-wrap'>
								{
									[...Array(5)].map((_, index) => 
										<img 
											className='instructor-profile-review-card-rating-star-image'
											key={`reviewStar${reviewRating}-${index}`}
											src={reviewQuickRepliesRating > index ? '/imgs/icons/icon-star.svg' : '/imgs/icons/icon-star-empty.svg'}
											alt='star'
											onClick={() => {
												setReviewQuickRepliesRating(index + 1)
											}}
										/>
									)
								}
							</div>
							<div className='instructor-profile-review-card-rating-name dashboard-content-area-review-review-card-rating-name'>Quick Replies Rating</div>
						</div>
						<div className='instructor-profile-review-card-rating-wrap'>
							<div className='instructor-profile-review-card-rating-star-wrap'>
								{
									[...Array(5)].map((_, index) => 
										<img 
											className='instructor-profile-review-card-rating-star-image'
											key={`reviewStar${reviewRating}-${index}`}
											src={reviewEncouragementRating > index ? '/imgs/icons/icon-star.svg' : '/imgs/icons/icon-star-empty.svg'}
											alt='star'
											onClick={() => {
												setReviewEncouragementRating(index + 1)
											}}
										/>
									)
								}
							</div>
							<div className='instructor-profile-review-card-rating-name dashboard-content-area-review-review-card-rating-name'>Encouragement Rating</div>
						</div>
					</div>
					<CtaButton
						label='Leave Review'
						disabled={leavingReview || inputMessage.length === 0}
						onClick={() => {
							leaveReviewButtonClicked()
						}}
						size='small'
					/>
				</div>
			}
			{
			// interactionStatus === InteractionStatus.COMPLETE && userStateData.isAnInstructor() === false &&
			requestTip && userStateData.isAnInstructor() === false &&
				<div className='dashboard-content-area-review-input-wrap'>
					<div className='dashboard-content-area-thank-you-message'>
						Interaction Complete - Thank you, would you like to leave a tip?
					</div>
					<div className='dashboard-content-area-tip-button-wrap'>
						<CtaButton
							label='No'
							onClick={() => {
								leaveTipClickHandler(0)
							}}
							size='medium'
						/>
						<CtaButton
							label='$5'
							onClick={() => {
								leaveTipClickHandler(500)
							}}
							size='medium'
						/>
						<CtaButton
							label='$10'
							onClick={() => {
								leaveTipClickHandler(1000)
							}}
							size='medium'
						/>
						<CtaButton
							label='$15'
							onClick={() => {
								leaveTipClickHandler(1500)
							}}
							size='medium'
						/>
					</div>
				</div>
			}
			{confirmSendingMessage &&
				<div className='confirm-sending-pop-up-wrap'>
					<div className='confirm-sending-pop-up'>
						<div className='confirm-sending-pop-up-txt'>Sending Message, Are you Sure?</div>
						<div className='confirm-sending-pop-up-button-wrap'>
							<CtaButton
								label='Cancel'
								onClick={() => {
									setConfirmSendingMessage(false)
								}}
								size='medium'
							/>
							<CtaButton
								label='Confirm'
								onClick={() => {
									setConfirmSendingMessage(false)
									confirmPopUpConfirm()
								}}
								size='medium'
							/>
						</div>
					</div>
				</div>
			}
		</div>
	)
}

export default DashboardChatContentArea
